import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { Textarea } from "../components/ui/textarea";
import {
  ClipboardCheck,
  Search,
  Download,
  Eye,
  Wrench,
  Truck,
  Calendar,
  User,
  Clock,
  FileText,
  Filter,
  CheckCircle,
  AlertCircle,
  Loader2,
  MapPin,
  XCircle,
  Package,
  Trash2,
  Siren,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ── Status buckets ────────────────────────────────────────────────────────
const ACTIVE_STATUSES   = ["pending", "confirmed", "in_progress", "worker_signed", "reserved", "approved", "pending_delivery"];
const IN_FIELD_STATUSES = ["delivered", "pending_return", "return_scheduled"];
const COMPLETED_STATUSES = ["completed", "returned"];
const CANCELLED_STATUSES = ["cancelled"];

// Which statuses allow an admin/manager to cancel
const CANCELLABLE_SERVICE = ["pending", "confirmed", "in_progress"];
const CANCELLABLE_RENTAL  = ["reserved", "approved", "pending_delivery", "delivered"];

const STATUS_FILTERS = [
  { value: "active",    label: "Active" },
  { value: "in_field",  label: "In Field" },
  { value: "completed", label: "Completed" },
  { value: "cancelled", label: "Cancelled" },
  { value: "all",       label: "All Records" },
];

const JobHistory = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [serviceJobCards, setServiceJobCards] = useState([]);
  const [rentalJobCards, setRentalJobCards] = useState([]);
  const [currentUser, setCurrentUser] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("active");
  const [typeFilter, setTypeFilter] = useState("all");
  const [workers, setWorkers] = useState([]);
  const [cancelDialog, setCancelDialog] = useState({ open: false, jobCard: null });
  const [cancelReason, setCancelReason] = useState("");
  const [cancelling, setCancelling] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState({ open: false, jobCard: null });
  const [deleting, setDeleting] = useState(false);

  // Helper functions - use actual status from database for consistency
  const getServiceStatus = (jc) => {
    // Use actual status from job card if available
    if (jc.status) return jc.status;
    // Fallback calculation for backward compatibility
    if (jc.is_locked || jc.customer_signature) return "completed";
    if (jc.job_started_at) return "in_progress";
    return "pending";
  };

  const getRentalStatus = (jc) => {
    // Use actual status from job card if available
    if (jc.status) return jc.status;
    // Fallback calculation for backward compatibility
    if (jc.return_customer_signature || jc.return_technician_signature) return "returned";
    if (jc.delivery_customer_signature || jc.delivery_technician_signature) return "delivered";
    return "pending_delivery";
  };

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [userRes, serviceRes, rentalRes] = await Promise.all([
        axios.get(`${API}/auth/me`, { withCredentials: true }),
        axios.get(`${API}/job-cards/all`, { withCredentials: true }),
        axios.get(`${API}/rental-job-cards/all`, { withCredentials: true }),
      ]);
      setCurrentUser(userRes.data);
      setServiceJobCards(serviceRes.data || []);
      setRentalJobCards(rentalRes.data || []);
      
      // Fetch workers for admin/manager to assign
      if (userRes.data?.role === "admin" || userRes.data?.role === "manager") {
        try {
          const workersRes = await axios.get(`${API}/users`, { withCredentials: true });
          const availableWorkers = workersRes.data.filter(
            (u) => u.is_active && (u.role === "worker" || u.role === "manager" || u.role === "admin")
          );
          setWorkers(availableWorkers);
        } catch (err) {
          console.log("Could not fetch workers list");
        }
      }
    } catch (error) {
      console.error("Error fetching job history:", error);
      toast.error("Failed to load job history");
    } finally {
      setLoading(false);
    }
  };

  // Normalize and combine job cards
  const combinedJobCards = useMemo(() => {
    const serviceCards = serviceJobCards.map((jc) => ({
      id: jc.booking_id,
      type: "service",
      typeLabel: jc.job_type === "callout" ? "Call-out" : "Service",
      typeIcon: jc.job_type === "callout" ? Siren : Wrench,
      typeBgColor: jc.job_type === "callout" ? "bg-red-100 text-red-700" : "bg-blue-100 text-blue-700",
      customerName: jc.customer_name || "Unknown",
      venueName: jc.venue_name || null,
      itemName: jc.service_name || "General Service",
      technicianId: jc.technician_id,
      technicianName: jc.technician_name || "Unassigned",
      date: jc.service_date || jc.created_at?.split("T")[0],
      status: getServiceStatus(jc),
      rawStatus: jc.status,
      isLocked: jc.is_locked,
      createdAt: jc.created_at,
      completedAt: jc.job_completed_at,
      route: `/job-card/${jc.booking_id}`,
    }));

    const rentalCards = rentalJobCards.map((jc) => ({
      id: jc.rental_id,
      type: "rental",
      typeLabel: "Rental",
      typeIcon: Truck,
      typeBgColor: "bg-amber-100 text-amber-700",
      customerName: jc.customer_name || "Unknown",
      venueName: jc.venue_name || null,
      itemName: jc.item_name || "Rental Item",
      technicianId: jc.technician_id,
      technicianName: jc.technician_name || "Unassigned",
      date: jc.start_date || jc.created_at?.split("T")[0],
      status: getRentalStatus(jc),
      rawStatus: jc.status,
      isLocked: jc.is_locked,
      createdAt: jc.created_at,
      completedAt: jc.return_signature?.signed_at,
      route: `/rental-job-card/${jc.rental_id}`,
    }));

    return [...serviceCards, ...rentalCards].sort((a, b) => 
      (b.createdAt || "").localeCompare(a.createdAt || "")
    );
  }, [serviceJobCards, rentalJobCards]);

  // Filter job cards
  const filteredJobCards = useMemo(() => {
    let filtered = combinedJobCards;

    // Type filter
    if (typeFilter !== "all") {
      filtered = filtered.filter((jc) => jc.type === typeFilter);
    }

    // Status filter
    if (statusFilter === "active") {
      filtered = filtered.filter((jc) => ACTIVE_STATUSES.includes(jc.status));
    } else if (statusFilter === "in_field") {
      filtered = filtered.filter((jc) => IN_FIELD_STATUSES.includes(jc.status));
    } else if (statusFilter === "completed") {
      filtered = filtered.filter((jc) => COMPLETED_STATUSES.includes(jc.status));
    } else if (statusFilter === "cancelled") {
      filtered = filtered.filter((jc) => CANCELLED_STATUSES.includes(jc.status));
    }
    // "all" — no filter

    // Search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter((jc) =>
        jc.customerName?.toLowerCase().includes(query) ||
        jc.id?.toLowerCase().includes(query) ||
        jc.itemName?.toLowerCase().includes(query) ||
        jc.technicianName?.toLowerCase().includes(query)
      );
    }

    return filtered;
  }, [combinedJobCards, statusFilter, typeFilter, searchQuery]);

  // Stats
  const stats = useMemo(() => {
    const all = combinedJobCards;
    return {
      total:     all.length,
      active:    all.filter((jc) => ACTIVE_STATUSES.includes(jc.status)).length,
      inField:   all.filter((jc) => IN_FIELD_STATUSES.includes(jc.status)).length,
      completed: all.filter((jc) => COMPLETED_STATUSES.includes(jc.status)).length,
      cancelled: all.filter((jc) => CANCELLED_STATUSES.includes(jc.status)).length,
    };
  }, [combinedJobCards]);

  const getStatusBadge = (status) => {
    const config = {
      // Service statuses
      completed:        { label: "Completed",       color: "bg-emerald-100 text-emerald-700", icon: CheckCircle },
      in_progress:      { label: "In Progress",     color: "bg-blue-100 text-blue-700",      icon: Loader2 },
      worker_signed:    { label: "Tech Signed",     color: "bg-indigo-100 text-indigo-700",  icon: CheckCircle },
      pending:          { label: "Pending",         color: "bg-amber-100 text-amber-700",    icon: Clock },
      confirmed:        { label: "Confirmed",       color: "bg-cyan-100 text-cyan-700",      icon: CheckCircle },
      cancelled:        { label: "Cancelled",       color: "bg-red-100 text-red-600",        icon: XCircle },
      // Rental statuses
      returned:         { label: "Returned",        color: "bg-emerald-100 text-emerald-700",icon: CheckCircle },
      delivered:        { label: "Delivered",       color: "bg-violet-100 text-violet-700",  icon: Package },
      pending_return:   { label: "Pending Return",  color: "bg-purple-100 text-purple-700",  icon: Clock },
      return_scheduled: { label: "Return Scheduled",color: "bg-orange-100 text-orange-700",  icon: Clock },
      pending_delivery: { label: "Pending Delivery",color: "bg-amber-100 text-amber-700",    icon: Clock },
      reserved:         { label: "Reserved",        color: "bg-slate-100 text-slate-600",    icon: Clock },
      approved:         { label: "Approved",        color: "bg-cyan-100 text-cyan-700",      icon: CheckCircle },
    };
    const cfg = config[status] || config.pending;
    const Icon = cfg.icon;
    return (
      <Badge className={`${cfg.color} rounded-sm gap-1 border-0`}>
        <Icon className="h-3 w-3" />
        {cfg.label}
      </Badge>
    );
  };

  const downloadPdf = async (jobCard) => {
    try {
      const endpoint = jobCard.type === "service" 
        ? `${API}/job-cards/${jobCard.id}/pdf`
        : `${API}/rental-job-cards/${jobCard.id}/pdf`;
      
      const response = await axios.get(endpoint, {
        responseType: "blob",
        withCredentials: true,
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `${jobCard.type}_job_card_${jobCard.id}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("PDF downloaded successfully");
    } catch (error) {
      console.error("Error downloading PDF:", error);
      toast.error("Failed to download PDF");
    }
  };

  const handleAssignTechnician = async (jobCard, workerId) => {
    if (!workerId || workerId === "unassigned") return;
    
    try {
      const endpoint = jobCard.type === "service"
        ? `${API}/service-bookings/${jobCard.id}/assign-worker`
        : `${API}/rentals/${jobCard.id}/assign-technician`;
      
      const payload = jobCard.type === "service"
        ? { worker_id: workerId }
        : { technician_id: workerId };
      
      await axios.post(endpoint, payload, { withCredentials: true });
      toast.success("Technician assigned successfully");
      fetchData();
    } catch (error) {
      console.error("Error assigning technician:", error);
      toast.error(error.response?.data?.detail || "Failed to assign technician");
    }
  };

  const isCancellable = (jobCard) => {
    if (jobCard.type === "service") return CANCELLABLE_SERVICE.includes(jobCard.status);
    if (jobCard.type === "rental")  return CANCELLABLE_RENTAL.includes(jobCard.status);
    return false;
  };

  const handleCancelConfirm = async () => {
    const { jobCard } = cancelDialog;
    if (!jobCard) return;
    setCancelling(true);
    try {
      const endpoint = jobCard.type === "service"
        ? `${API}/service-bookings/${jobCard.id}/cancel`
        : `${API}/rentals/${jobCard.id}/cancel`;
      await axios.post(endpoint, { reason: cancelReason }, { withCredentials: true });
      toast.success(`${jobCard.type === "service" ? "Booking" : "Rental"} cancelled successfully`);
      setCancelDialog({ open: false, jobCard: null });
      setCancelReason("");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to cancel");
    } finally {
      setCancelling(false);
    }
  };

  const handleDeleteConfirm = async () => {
    const { jobCard } = deleteDialog;
    if (!jobCard) return;
    setDeleting(true);
    try {
      const endpoint = jobCard.type === "service"
        ? `${API}/service-bookings/${jobCard.id}`
        : `${API}/rentals/${jobCard.id}`;
      await axios.delete(endpoint, { withCredentials: true });
      toast.success(`${jobCard.type === "service" ? "Service booking" : "Rental"} deleted permanently`);
      setDeleteDialog({ open: false, jobCard: null });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete");
    } finally {
      setDeleting(false);
    }
  };

  if (loading) {
    return (
      <div className="p-6 md:p-8 flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin text-slate-400 mx-auto mb-2" />
          <p className="text-slate-500">Loading job history...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 md:p-8" data-testid="job-history-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900 tracking-tight flex items-center gap-2">
            <ClipboardCheck className="h-6 w-6" />
            Job History
          </h1>
          <p className="text-slate-500">View and manage all service and rental job cards</p>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
        <Card className="rounded-sm cursor-pointer hover:border-slate-400 transition-colors" onClick={() => setStatusFilter("all")}>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Total Jobs</p>
                <p className="text-2xl font-bold">{stats.total}</p>
              </div>
              <FileText className="h-8 w-8 text-slate-300" />
            </div>
          </CardContent>
        </Card>
        <Card className={`rounded-sm cursor-pointer transition-colors hover:border-blue-400 ${statusFilter === "active" ? "border-blue-400 bg-blue-50" : ""}`} onClick={() => setStatusFilter("active")}>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Active</p>
                <p className="text-2xl font-bold text-blue-600">{stats.active}</p>
              </div>
              <Loader2 className="h-8 w-8 text-blue-200" />
            </div>
          </CardContent>
        </Card>
        <Card className={`rounded-sm cursor-pointer transition-colors hover:border-violet-400 ${statusFilter === "in_field" ? "border-violet-400 bg-violet-50" : ""}`} onClick={() => setStatusFilter("in_field")}>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">In Field</p>
                <p className="text-2xl font-bold text-violet-600">{stats.inField}</p>
              </div>
              <Package className="h-8 w-8 text-violet-200" />
            </div>
          </CardContent>
        </Card>
        <Card className={`rounded-sm cursor-pointer transition-colors hover:border-emerald-400 ${statusFilter === "completed" ? "border-emerald-400 bg-emerald-50" : ""}`} onClick={() => setStatusFilter("completed")}>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Completed</p>
                <p className="text-2xl font-bold text-emerald-600">{stats.completed}</p>
              </div>
              <CheckCircle className="h-8 w-8 text-emerald-200" />
            </div>
          </CardContent>
        </Card>
        <Card className={`rounded-sm cursor-pointer transition-colors hover:border-red-400 ${statusFilter === "cancelled" ? "border-red-400 bg-red-50" : ""}`} onClick={() => setStatusFilter("cancelled")}>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Cancelled</p>
                <p className="text-2xl font-bold text-red-500">{stats.cancelled}</p>
              </div>
              <XCircle className="h-8 w-8 text-red-200" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card className="rounded-sm mb-6">
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  placeholder="Search by customer, ID, item, or technician..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                  data-testid="job-history-search"
                />
              </div>
            </div>
            <div className="flex gap-2">
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-[200px]" data-testid="status-filter">
                  <Filter className="h-4 w-4 mr-2" />
                  <SelectValue placeholder="Filter by status" />
                </SelectTrigger>
                <SelectContent>
                  {STATUS_FILTERS.map((filter) => (
                    <SelectItem key={filter.value} value={filter.value}>
                      {filter.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={typeFilter} onValueChange={setTypeFilter}>
                <SelectTrigger className="w-[150px]" data-testid="type-filter">
                  <SelectValue placeholder="Type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Types</SelectItem>
                  <SelectItem value="service">Service Only</SelectItem>
                  <SelectItem value="rental">Rental Only</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Results Info */}
      {statusFilter === "in_field" && (
        <div className="mb-4 flex items-center gap-2 text-sm text-slate-500">
          <Package className="h-4 w-4" />
          Showing rental items currently at customer sites (Delivered, Pending Return, Return Scheduled).
        </div>
      )}

      {/* Job Cards Table */}
      <Card className="rounded-sm">
        <CardHeader>
          <CardTitle className="text-lg flex items-center justify-between">
            <span>Job Cards ({filteredJobCards.length})</span>
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {filteredJobCards.length > 0 ? (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Type</TableHead>
                  <TableHead>ID</TableHead>
                  <TableHead>Customer</TableHead>
                  <TableHead>Item/Service</TableHead>
                  <TableHead>Technician</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredJobCards.map((jobCard) => {
                  const TypeIcon = jobCard.typeIcon;
                  return (
                    <TableRow 
                      key={`${jobCard.type}-${jobCard.id}`}
                      className="cursor-pointer hover:bg-slate-50"
                      onClick={() => navigate(jobCard.route)}
                      data-testid={`job-row-${jobCard.id}`}
                    >
                      <TableCell>
                        <Badge className={`${jobCard.typeBgColor} rounded-sm gap-1`}>
                          <TypeIcon className="h-3 w-3" />
                          {jobCard.typeLabel}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <code className="text-xs bg-slate-100 px-2 py-0.5 rounded">
                          {jobCard.id?.slice(0, 20)}...
                        </code>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <User className="h-4 w-4 text-slate-400" />
                          <div>
                            <div>{jobCard.customerName}</div>
                            {jobCard.venueName && (
                              <div className="text-xs text-blue-600 flex items-center gap-1">
                                <MapPin className="h-3 w-3" />{jobCard.venueName}
                              </div>
                            )}
                          </div>
                        </div>
                      </TableCell>
                      <TableCell className="font-medium">{jobCard.itemName}</TableCell>
                      <TableCell onClick={(e) => e.stopPropagation()}>
                        {(currentUser?.role === "admin" || currentUser?.role === "manager") && !jobCard.isLocked ? (
                          <Select
                            value={jobCard.technicianId || "unassigned"}
                            onValueChange={(value) => handleAssignTechnician(jobCard, value)}
                          >
                            <SelectTrigger className="h-7 w-[140px] text-sm" data-testid={`assign-tech-${jobCard.id}`}>
                              <SelectValue placeholder="Assign" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="unassigned">Unassigned</SelectItem>
                              {workers.map((worker) => (
                                <SelectItem key={worker.user_id} value={worker.user_id}>
                                  {worker.name}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        ) : (
                          jobCard.technicianName
                        )}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Calendar className="h-4 w-4 text-slate-400" />
                          {jobCard.date}
                        </div>
                      </TableCell>
                      <TableCell>{getStatusBadge(jobCard.status)}</TableCell>
                      <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center justify-end gap-1">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => navigate(jobCard.route)}
                            title="View Job Card"
                            data-testid={`view-btn-${jobCard.id}`}
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => downloadPdf(jobCard)}
                            title="Download PDF"
                            data-testid={`pdf-btn-${jobCard.id}`}
                          >
                            <Download className="h-4 w-4" />
                          </Button>
                          {(currentUser?.role === "admin" || currentUser?.role === "manager") && isCancellable(jobCard) && (
                            <Button
                              size="sm"
                              variant="ghost"
                              className="text-red-500 hover:text-red-700 hover:bg-red-50"
                              onClick={() => { setCancelDialog({ open: true, jobCard }); setCancelReason(""); }}
                              title="Cancel Job"
                              data-testid={`cancel-btn-${jobCard.id}`}
                            >
                              <XCircle className="h-4 w-4" />
                            </Button>
                          )}
                          {currentUser?.role === "admin" && (
                            <Button
                              size="sm"
                              variant="ghost"
                              className="text-red-600 hover:text-red-800 hover:bg-red-100"
                              onClick={() => setDeleteDialog({ open: true, jobCard })}
                              title="Delete Permanently"
                              data-testid={`delete-btn-${jobCard.id}`}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          ) : (
            <div className="text-center py-12">
              <ClipboardCheck className="h-12 w-12 text-slate-300 mx-auto mb-3" />
              <p className="text-slate-500">No job cards found</p>
              <p className="text-sm text-slate-400">
                {searchQuery ? "Try adjusting your search or filters" : "Job cards will appear here once created"}
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Cancel Confirmation Dialog */}
      <AlertDialog
        open={cancelDialog.open}
        onOpenChange={(open) => { if (!open) { setCancelDialog({ open: false, jobCard: null }); setCancelReason(""); } }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-red-600">
              <XCircle className="h-5 w-5" />
              Cancel {cancelDialog.jobCard?.type === "rental" ? "Rental" : "Service Booking"}
            </AlertDialogTitle>
            <AlertDialogDescription>
              You are about to cancel{" "}
              <span className="font-semibold text-slate-700">
                {cancelDialog.jobCard?.itemName}
              </span>{" "}
              for{" "}
              <span className="font-semibold text-slate-700">
                {cancelDialog.jobCard?.customerName}
              </span>.
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="px-1 pb-2">
            <label className="text-sm font-medium text-slate-700 mb-1.5 block">
              Reason for cancellation <span className="text-slate-400 font-normal">(optional)</span>
            </label>
            <Textarea
              value={cancelReason}
              onChange={(e) => setCancelReason(e.target.value)}
              placeholder="e.g. Customer requested cancellation, scheduling conflict..."
              rows={3}
              className="rounded-sm text-sm"
            />
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-sm" disabled={cancelling}>
              Keep Job
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleCancelConfirm}
              disabled={cancelling}
              className="rounded-sm bg-red-600 hover:bg-red-700 text-white"
            >
              {cancelling ? (
                <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Cancelling...</>
              ) : (
                "Confirm Cancellation"
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete Confirmation Dialog (admin only) */}
      <AlertDialog
        open={deleteDialog.open}
        onOpenChange={(open) => { if (!open) setDeleteDialog({ open: false, jobCard: null }); }}
      >
        <AlertDialogContent data-testid="delete-job-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-red-700">
              <Trash2 className="h-5 w-5" />
              Delete {deleteDialog.jobCard?.type === "rental" ? "Rental" : "Service Booking"}?
            </AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete{" "}
              <span className="font-semibold text-slate-700">
                {deleteDialog.jobCard?.itemName}
              </span>{" "}
              for{" "}
              <span className="font-semibold text-slate-700">
                {deleteDialog.jobCard?.customerName}
              </span>{" "}
              and its job card. This action <span className="font-semibold">cannot be undone</span>.
              {deleteDialog.jobCard?.type === "rental" && (
                <span className="block mt-2 text-xs text-slate-500">
                  Inventory will be restored if the rental has not yet been returned.
                </span>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-sm" disabled={deleting} data-testid="delete-cancel-btn">
              Keep
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteConfirm}
              disabled={deleting}
              className="rounded-sm bg-red-700 hover:bg-red-800 text-white"
              data-testid="delete-confirm-btn"
            >
              {deleting ? (
                <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Deleting...</>
              ) : (
                "Delete Permanently"
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default JobHistory;
